import React, { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Link,
  FormHelperText,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FormatListBulleted as ListIcon, OpenInNew, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { notificationChannelApi, CanmailList } from '../../../../../api/cansense/notificationChannelApi';

// ============================================================================
// Types
// ============================================================================

// Extended interface to support optional description from future API enhancements
interface CanmailListWithDescription extends CanmailList {
  description?: string;
}

interface CANmailListSelectorProps {
  value: number | null;
  onChange: (listId: number | null) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  showManageLink?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * CANmailListSelector - A dropdown component for selecting CANmail distribution lists.
 *
 * Features:
 * - Fetches available CANmail lists from the API
 * - Displays list name with member count
 * - Optional link to manage lists in CANmail
 * - Loading and error states
 */
export const CANmailListSelector: React.FC<CANmailListSelectorProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  helperText,
  showManageLink = true
}) => {
  const theme = useTheme();

  // State
  const [lists, setLists] = useState<CanmailListWithDescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Fetch CANmail lists
  const fetchLists = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setFetchError(null);
      const data = await notificationChannelApi.getCanmailLists();
      setLists(data);
    } catch (err) {
      console.error('Failed to fetch CANmail lists:', err);
      setFetchError(
        err instanceof Error ? err.message : 'Failed to load CANmail lists'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  // Get selected list details
  const selectedList = lists.find((list) => list.id === value);

  // Loading state
  if (loading) {
    return (
      <FormControl fullWidth>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        {helperText && (
          <FormHelperText>
            <Skeleton width="60%" />
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {fetchError}
        <Box sx={{ mt: 1 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => fetchLists()}
          >
            Retry
          </Link>
        </Box>
      </Alert>
    );
  }

  return (
    <Box>
      <FormControl fullWidth error={!!error} disabled={disabled}>
        <InputLabel>
          CANmail List {required && '*'}
        </InputLabel>
        <Select
          value={value ?? ''}
          onChange={(e) => {
            const newValue = e.target.value;
            onChange(newValue === '' ? null : (newValue as number));
          }}
          label={`CANmail List ${required ? '*' : ''}`}
          renderValue={(selected) => {
            const list = lists.find((l) => l.id === selected);
            return list ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                <Typography component="span">
                  {list.name}
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: 'text.secondary' }}
                >
                  ({list.memberCount} members)
                </Typography>
              </Box>
            ) : '';
          }}
        >
          {lists.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No CANmail lists available
              </Typography>
            </MenuItem>
          ) : (
            lists.map((list) => (
              <MenuItem key={list.id} value={list.id}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                  <ListIcon
                    sx={{
                      mr: 1.5,
                      mt: 0.5,
                      fontSize: 20,
                      color: alpha(theme.palette.primary.main, 0.7)
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {list.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {list.memberCount} {list.memberCount === 1 ? 'member' : 'members'}
                      </Typography>
                      {list.description && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            &bull;
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {list.description}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </Select>
        {(helperText || error) && (
          <FormHelperText error={!!error}>
            {error || helperText}
          </FormHelperText>
        )}
      </FormControl>

      {showManageLink && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link
            href="/canappsv3/listmanager"
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
          >
            Manage lists in CANmail
            <OpenInNew sx={{ ml: 0.5, fontSize: 14 }} />
          </Link>
          <Tooltip title="Refresh lists">
            <IconButton
              size="small"
              onClick={() => fetchLists(true)}
              disabled={refreshing}
              sx={{ color: 'text.secondary' }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default CANmailListSelector;
